import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { Sun, Droplet, Leaf, Wrench, Euro, Thermometer } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

const Zonneboilers = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Zonneboiler voor warm water | SOL-VI"
          description="Bespaar gas met een zonneboiler. Gratis warm water op zonnestraling, lage onderhoudskosten en betrouwbare installatie."
          type="website"
        />
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Zonneboiler: Gratis Warm Water door de Kracht van de Zon
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Verwarm uw water met de zon en bespaar op uw gaskosten
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                  Vraag Gratis Offerte
                </Button>
                <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                  Plan Adviesgesprek
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Wist u dat een groot deel van uw gasverbruik opgaat aan het verwarmen van water voor de douche, het bad en de keuken? Met een zonneboiler van SOL-VI gebruikt u de gratis warmte van de zon om uw water te verwarmen. Het is een eenvoudige, uiterst betrouwbare en milieuvriendelijke manier om direct te besparen op uw gasrekening.
                </p>
              </div>

              {/* How it works */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Hoe werkt een zonneboiler?</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Een zonneboiler bestaat uit zonnecollectoren op uw dak en een voorraadvat voor water in uw huis. Een vloeistof in de collectoren wordt opgewarmd door de zon. Deze warmte wordt vervolgens via een leiding naar het voorraadvat getransporteerd, waar het uw tapwater verwarmt. Mocht de zon niet genoeg warmte leveren, dan zorgt uw CV-ketel of warmtepomp voor de naverwarming, zodat u nooit zonder warm water zit.
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">De voordelen van een SOL-VI zonneboiler:</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Droplet className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Gratis Warm Water</h3>
                      <p className="text-muted-foreground">De zon levert de energie. Een zonneboiler kan tot wel 60% van uw energieverbruik voor warm water besparen.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Leaf className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Milieuvriendelijk</h3>
                      <p className="text-muted-foreground">U vermindert direct uw gasverbruik en daarmee uw CO2-uitstoot. Een duurzame keuze voor het milieu.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Wrench className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Lage Onderhoudskosten</h3>
                      <p className="text-muted-foreground">Een zonneboilersysteem is robuust, heeft weinig bewegende delen en vereist minimaal onderhoud.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Sun className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Werkt het hele jaar door</h3>
                      <p className="text-muted-foreground">Ook op een bewolkte dag is er genoeg straling om het water voor te verwarmen, waardoor uw CV-ketel minder hard hoeft te werken.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Process */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">Onze Werkwijze: Zonnewarmte voor uw Huis</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Gratis Advies</h3>
                      <p className="text-muted-foreground">We bekijken uw warmwaterverbruik en de ligging van uw dak om de juiste grootte van de collector en het voorraadvat te bepalen.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Offerte op Maat</h3>
                      <p className="text-muted-foreground">U ontvangt een heldere offerte, inclusief de mogelijke besparingen en informatie over subsidies (ISDE).</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Vakkundige Installatie</h3>
                      <p className="text-muted-foreground">Onze loodgieters en installateurs zorgen voor een perfecte installatie en aansluiting op uw bestaande watersysteem.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Direct Besparen</h3>
                      <p className="text-muted-foreground">Na installatie geniet u direct van warm water, verwarmd door de gratis kracht van de zon.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div className="text-center bg-muted/50 rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4">Klaar voor een warme douche op zonne-energie?</h2>
                <p className="text-muted-foreground mb-6">Verlaag uw gasverbruik met een slimme en duurzame zonneboiler.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                    Vraag een vrijblijvende offerte aan
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                    Plan uw gratis adviesgesprek
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Zonneboilers;